/**
 * Forms Settings → New Patients — the DEFAULT messages, the rule cards, the validation, the switch card's block reasons and the
 * rule matcher the sender twins. Pure.
 * Plan: docs/plans/2026-10-20-forms-settings-plan.md §0 (D2, D4, D15), §1h (the decided default text), §2.2 (data), §2.5.
 *
 * ⛔ THE BLOB is teamio.settings Property 'NEW_PATIENT_FORMS_SETTINGS'. Absent ⇒ OFF. Every flag is the 'TRUE'/'FALSE' STRING, read
 *    through isOn. `enabled` and `liveSince` are written ONLY by the switch card's confirm (liveSince by the SERVER); an editor Save
 *    never carries them (D15a).
 *
 * ⛔ NEW_PATIENT_DEFAULTS IS ONE CONSTANT IN THREE REPOS (later): forms-service lib/formsSettings/defaults.js (the sender's fallback),
 *    teamio-admin (the onboarding seeder, WP3) and THIS file ("Reset to default"). The twin test in forms-service fails — never
 *    skips — when the two drift; teamio-admin gets its own in WP3. Change all copies together. Key order is part of the value.
 *
 * ⛔ DEFAULT TEXTS FIT ONE CREDIT: with Mint Dental / Christopher / 10-21-2026 10:00 AM and the 33-character sample link the text
 *    is 149 characters. The tokens are the real ones every Teamio template uses; there is no signature token (the layout appends it).
 *
 * ⛔ CODE MATCHING HERE IS A PLAIN STRING COMPARE (trimmed, both sides). The SENDER (forms-service WP1) must match an appointment's
 *    procedures against a rule's codes with lib/appointmentProcedures.js — it knows both the CodeNum and the ProcCode of every
 *    procedure; this file only ever sees the strings it is handed. Never write a second code matcher in the sender.
 *
 * ⛔ IMPORTS: only ./formLink (see the header there). Top-level names must stay unique across formLink.js / newPatient.js / medHx.js.
 */

import { isOn, isPlainObject, validateMessage, SHARED_BLOCK_MESSAGES } from './formLink';

export const NEW_PATIENT_SETTINGS_PROPERTY = 'NEW_PATIENT_FORMS_SETTINGS';

export const NEW_PATIENT_DEFAULT_SMS_BODY =
  '{{site_name}}: Hi {{patient_first_name}}, please fill out your new patient forms before your appointment on {{apt_date}}: {{form_link}}';

export const NEW_PATIENT_DEFAULT_EMAIL_SUBJECT = 'Your Dental Appointment';

export const NEW_PATIENT_DEFAULT_EMAIL_BODY =
  'We\'re excited to meet you!\n\nYour appointment is scheduled for {{apt_date}}.\n\nPlease arrive ten minutes prior to your appointment to complete your forms, or to save yourself some time, just click the link below to get started.\n\n{{form_link}}';

export const NEW_PATIENT_DEFAULTS = Object.freeze({
  enabled: 'FALSE',
  liveSince: null,
  mode: 'all',
  everyone: Object.freeze({
    sms: Object.freeze({ on: 'TRUE', body: NEW_PATIENT_DEFAULT_SMS_BODY, formLink: null }),
    email: Object.freeze({
      on: 'TRUE',
      subject: NEW_PATIENT_DEFAULT_EMAIL_SUBJECT,
      body: NEW_PATIENT_DEFAULT_EMAIL_BODY,
      formLink: null,
    }),
  }),
  rules: Object.freeze([]),
});

/** 'all' = one text + one email for every new patient; 'rules' = rule cards, then "Everyone else" (the general message). */
export const NEW_PATIENT_MODES = Object.freeze(['all', 'rules']);

/** A new rule card, pre-filled with the default messages (both channels ON, no codes yet). Fresh and mutable — the editor edits it. */
export function newRule(id) {
  return {
    id,
    label: '',
    codes: [],
    providerNums: [],
    sms: { on: 'TRUE', body: NEW_PATIENT_DEFAULT_SMS_BODY, formLink: null },
    email: { on: 'TRUE', subject: NEW_PATIENT_DEFAULT_EMAIL_SUBJECT, body: NEW_PATIENT_DEFAULT_EMAIL_BODY, formLink: null },
  };
}

export const NEW_PATIENT_PROBLEM_TEXT = Object.freeze({
  MODE_INVALID: 'Choose All new patients or Specific procedures.',
  RULE_NO_CODES: 'Add at least one procedure code.',
  RULE_NO_CHANNEL: 'Turn on Text or Email for this rule.',
});

/** A rule's codes / providers as trimmed, non-empty strings (numbers allowed — the provider picker stores ProvNums). */
function cleanList(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((x) => typeof x === 'string' || typeof x === 'number')
    .map((x) => String(x).trim())
    .filter((x) => x !== '');
}

function ruleList(blob) {
  return blob.mode === 'rules' && Array.isArray(blob.rules) ? blob.rules.filter(isPlainObject) : [];
}

/**
 * The editable blob → every problem, as [{ path, code, message }]:
 *   mode                   MODE_INVALID
 *   everyone.sms / .email  validateMessage's codes
 *   rules[i].codes         RULE_NO_CODES        (mode 'rules' only — rules are ignored in mode 'all')
 *   rules[i].channels      RULE_NO_CHANNEL
 *   rules[i].sms / .email  validateMessage's codes
 * `path` names the message, not the box inside it: call validateMessage on the one message for the per-field detail.
 */
export function validateNewPatientSettings(blob) {
  const b = isPlainObject(blob) ? blob : {};
  const problems = [];
  const add = (path, code, message) => problems.push({ path, code, message });
  const addMessage = (path, msg, channel) => {
    validateMessage(msg, channel).forEach((p) => add(path, p.code, p.message));
  };

  if (NEW_PATIENT_MODES.indexOf(b.mode) === -1) add('mode', 'MODE_INVALID', NEW_PATIENT_PROBLEM_TEXT.MODE_INVALID);

  const everyone = isPlainObject(b.everyone) ? b.everyone : {};
  addMessage('everyone.sms', everyone.sms, 'sms');
  addMessage('everyone.email', everyone.email, 'email');

  if (b.mode === 'rules' && Array.isArray(b.rules)) {
    b.rules.forEach((rule, i) => {
      const r = isPlainObject(rule) ? rule : {};
      const at = `rules[${i}]`;
      if (cleanList(r.codes).length === 0) add(`${at}.codes`, 'RULE_NO_CODES', NEW_PATIENT_PROBLEM_TEXT.RULE_NO_CODES);
      const smsOn = isPlainObject(r.sms) && isOn(r.sms.on);
      const emailOn = isPlainObject(r.email) && isOn(r.email.on);
      if (!smsOn && !emailOn) add(`${at}.channels`, 'RULE_NO_CHANNEL', NEW_PATIENT_PROBLEM_TEXT.RULE_NO_CHANNEL);
      addMessage(`${at}.sms`, r.sms, 'sms');
      addMessage(`${at}.email`, r.email, 'email');
    });
  }
  return problems;
}

/** The workflow sentence of the switch card, with the first active workflow's name in curly quotes. */
export function workflowSendsMessage(firstName) {
  return `New patients already get their forms from the workflow ‘${firstName}’. Turn that workflow off first.`;
}

/**
 * Why the New Patients switch cannot be turned on — every reason that applies, in the order the card shows them (the FIRST is the
 * one shown). Evaluated as if `enabled` were TRUE: this is the "can it be turned on" check.
 *   ctx = { activeWorkflowNames:[String], smsReady:Boolean, emailReady:Boolean }
 *   WORKFLOW_SENDS    an active workflow already sends new patients their forms (D15b — beats everything)
 *   MESSAGE_PROBLEMS  validateNewPatientSettings found anything
 *   SMS_NOT_READY     a Text message is on (everyone, or any rule in mode 'rules') and the site has no texting number
 *   EMAIL_NOT_READY   an Email message is on and the site has no verified sender
 *   NOTHING_ON        no message is on at all
 * @returns {Array<{ code:string, message:string }>}
 */
export function newPatientBlockReasons(blob, ctx) {
  const b = isPlainObject(blob) ? blob : {};
  const c = isPlainObject(ctx) ? ctx : {};
  const reasons = [];

  if (Array.isArray(c.activeWorkflowNames) && c.activeWorkflowNames.length > 0) {
    const named = c.activeWorkflowNames.filter((n) => typeof n === 'string' && n.trim() !== '');
    reasons.push({ code: 'WORKFLOW_SENDS', message: workflowSendsMessage(named.length ? named[0].trim() : 'Untitled workflow') });
  }

  if (validateNewPatientSettings(b).length > 0) {
    reasons.push({ code: 'MESSAGE_PROBLEMS', message: SHARED_BLOCK_MESSAGES.MESSAGE_PROBLEMS });
  }

  const everyone = isPlainObject(b.everyone) ? b.everyone : {};
  const inScope = [everyone].concat(ruleList(b));
  const anyOn = (channel) => inScope.some((card) => isPlainObject(card[channel]) && isOn(card[channel].on));
  const smsOn = anyOn('sms');
  const emailOn = anyOn('email');

  if (smsOn && !c.smsReady) reasons.push({ code: 'SMS_NOT_READY', message: SHARED_BLOCK_MESSAGES.SMS_NOT_READY });
  if (emailOn && !c.emailReady) reasons.push({ code: 'EMAIL_NOT_READY', message: SHARED_BLOCK_MESSAGES.EMAIL_NOT_READY });
  if (!smsOn && !emailOn) reasons.push({ code: 'NOTHING_ON', message: SHARED_BLOCK_MESSAGES.NOTHING_ON });
  return reasons;
}

/**
 * Which messages does this appointment get? The rule matcher the sender twins.
 *   appointment = { codes:[String], providerNums:[String] }
 *   mode 'rules' → the FIRST rule (array order) where any code matches AND (the rule has no providers OR any provider matches);
 *   no match, or any other mode → "Everyone else" (the `everyone` card).
 * @returns {{ source:'everyone'|'rule', ruleId?:string, sms:object|null, email:object|null }} the stored message objects themselves
 *   (not copies) — the caller still checks each message's `on`; a message that is off sends nothing.
 */
export function pickNewPatientMessages(blob, appointment) {
  const b = isPlainObject(blob) ? blob : {};
  const apt = isPlainObject(appointment) ? appointment : {};
  const codes = cleanList(apt.codes);
  const providers = cleanList(apt.providerNums);

  if (b.mode === 'rules') {
    const hit = ruleList(b).find((rule) => {
      const ruleCodes = cleanList(rule.codes);
      if (!ruleCodes.some((code) => codes.indexOf(code) !== -1)) return false;
      const ruleProviders = cleanList(rule.providerNums);
      return ruleProviders.length === 0 || ruleProviders.some((p) => providers.indexOf(p) !== -1);
    });
    if (hit) {
      return {
        source: 'rule',
        ruleId: hit.id,
        sms: isPlainObject(hit.sms) ? hit.sms : null,
        email: isPlainObject(hit.email) ? hit.email : null,
      };
    }
  }
  const everyone = isPlainObject(b.everyone) ? b.everyone : {};
  return {
    source: 'everyone',
    sms: isPlainObject(everyone.sms) ? everyone.sms : null,
    email: isPlainObject(everyone.email) ? everyone.email : null,
  };
}
